/**
 * Which chunk a prerendered route needs in hand BEFORE hydration.
 *
 * WHY THIS EXISTS
 *
 * Hydration that reaches a Suspense boundary whose chunk has not arrived tears
 * the prerendered content out and paints it again, which undoes prerendering
 * entirely. React named it once the App-level Suspense was removed:
 *
 *     Expected server HTML to contain a matching <section> in <div>
 *         at Pricings
 *         at Suspense
 *
 * Everywhere that is a text page, the answer is to import it eagerly, which is
 * MainRoute.js's own convention for prerendered routes. The /social family
 * cannot be: it is an application surface rather than a page of text, and the
 * entry bundle is already 3.2MB, so pulling it in would charge every visitor
 * who never opens it. Its chunk is fetched before hydrateRoot instead, and only
 * for the route actually being visited.
 *
 * WHY IT IS ITS OWN FILE
 *
 * Two reasons, and the second is the one that forced it. Mapping a path to a
 * chunk is not the same job as declaring the route tree. And importing
 * MainRoute.js in a test pulls the entire application graph, which jest cannot
 * even parse (uuid ships ESM only, Demopage builds a worker with
 * `new URL(..., import.meta.url)`), so the matching could not be tested where
 * it lived.
 *
 * Each loader is named once here and used twice, by lazyRetry in MainRoute.js
 * and by the table below, so the two cannot drift.
 */

export const loadSocialHome = () => import('./components/Social/SocialHome');
export const loadSocialFeed = () => import('./components/Social/Feed/FeedPage');
export const loadCommunityListPage = () =>
  import('./components/Social/Communities/CommunityListPage');
export const loadChallengesPage = () =>
  import('./components/Social/Gamification/ChallengesPage');
export const loadRecipeListPage = () => import('./components/Social/Recipes/RecipeListPage');
export const loadGameHub = () => import('./components/Social/Games/GameHub');
export const loadKidsLearningHub = () =>
  import('./components/Social/KidsLearning/KidsLearningHub');

// Only the /social paths scripts/prerender.js actually builds. A route with no
// prerendered file is served the shell, takes the createRoot branch in
// index.js, and never hydrates, so preloading for it would buy nothing.
export const PRERENDERED_CHUNKS = [
  ['/social', loadSocialFeed],
  ['/social/communities', loadCommunityListPage],
  ['/social/challenges', loadChallengesPage],
  ['/social/recipes', loadRecipeListPage],
  ['/social/games', loadGameHub],
  ['/social/kids', loadKidsLearningHub],
];

// The shell every /social route renders inside. A leaf alone is not enough:
// this boundary would still suspend and take the page with it.
export const SOCIAL_SHELL = loadSocialHome;

/**
 * The loaders a path needs, most specific first.
 *
 * Separated from the fetching so it can be tested with a stub table. The real
 * loaders pull application code that jest cannot parse, so a test that called
 * them would be testing the bundler, not the matching.
 *
 * @param {string} pathname
 * @param {Array<[string, Function]>} [table]
 * @param {Function} [shell]
 * @returns {Function[]} possibly empty
 */
export function selectChunkLoaders(pathname, table = PRERENDERED_CHUNKS, shell = SOCIAL_SHELL) {
  if (typeof pathname !== 'string') return [];
  // Longest prefix wins. '/social' is a prefix of all of them, so matching the
  // first hit would preload the feed for every sub-route and leave the real one
  // suspending, which is the bug this is here to avoid.
  const hit = table
    .filter(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))
    .sort((a, b) => b[0].length - a[0].length)[0];
  if (!hit) return [];
  return hit[1] === shell ? [hit[1]] : [hit[1], shell];
}

/**
 * Fetch this route's chunk. Never rejects: hydrating without it is exactly the
 * behaviour that existed before, so a failure leaves things no worse.
 */
export function preloadPrerenderedChunk(pathname) {
  const loaders = selectChunkLoaders(pathname);
  if (!loaders.length) return Promise.resolve([]);
  return Promise.all(loaders.map((load) => load())).catch(() => []);
}
